import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { t, wanneer, geleden } from '../i18n.js';
import { St } from '../components/Status.jsx';
import BlokkadeVoorstellen from '../components/BlokkadeVoorstellen.jsx';

/**
 * Blokkades (docs/besluiten/blokkades-bouwen.md), zoals in het prototype: wat wacht op goedkeuring, wat actief is, en
 * de historie. ZERO stelt voor, een medewerker keurt goed, ZERO schrijft met een eigen account; elke blokkade vervalt.
 */
/** De stand van een blokkade: de kleur en het woord. */
export const blokStand = (s) => ({
  wacht: ['warning', t('wacht')], actief: ['critical', t('actief')], afgewezen: ['neutral', t('afgewezen')], opgeheven: ['good', t('opgeheven')],
  verlopen: ['good', t('verlopen')], mislukt: ['serious', t('mislukt')],
}[s] ?? ['neutral', s]);
const tot = (iso) => {
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return t('nu');
  const u = Math.round(ms / 3_600_000);
  return u < 48 ? t('over {n} uur', { n: Math.max(1, u) }) : t('over {n} dagen', { n: Math.round(u / 24) });
};

export default function Blokkades({ mij, klant }) {
  const [tab, zetTab] = useState('wacht');
  const naar = useNavigate();
  const q = useQuery({ queryKey: ['blokkades', klant], queryFn: () => api.blokkades(klant), refetchInterval: 30_000 });
  const groepen = { wacht: (b) => b.status === 'wacht', actief: (b) => b.status === 'actief', historie: (b) => !['wacht', 'actief'].includes(b.status) };
  const lijst = q.data?.data ?? [];
  const klanten = mij.tenants.filter((k) => klant === 'alle' || String(k.id) === String(klant));
  return (
    <>
      <div className="page-head"><div>
        <h1>{t('Blokkades')}</h1>
        <p className="sub">{t('ADTS ZERO stelt voor, een medewerker keurt goed, ADTS ZERO schrijft met een eigen account. Elke blokkade vervalt vanzelf.')}</p>
      </div></div>
      <BlokkadeVoorstellen mij={mij} klanten={klanten} />
      <div className="filters" style={{ marginTop: 14 }}>
        {[['wacht', 'Wachten op goedkeuring'], ['actief', 'Actief'], ['historie', 'Historie']].map(([k, l]) => (
          <button key={k} type="button" className="chip" aria-pressed={tab === k} onClick={() => zetTab(k)}>{t(l)} ({lijst.filter(groepen[k]).length})</button>))}
      </div>
      <section className="card">
        {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : (
          <table>
            <thead><tr><th>{t('Adres')}</th><th>{t('Reden')}</th><th className="hide-sm">{t('Firewalls')}</th><th>{t('Status')}</th>
              <th className="hide-sm">{tab === 'actief' ? t('Vervalt') : t('Sinds')}</th></tr></thead>
            <tbody>{lijst.filter(groepen[tab]).map((b) => (
              <tr key={b.number} className="klik" onClick={() => naar(`/blokkades/${b.number}`)}>
                <td><b className="mono">{b.ip}</b><br /><span className="muted mono" style={{ fontSize: 12 }}>{b.number}</span></td>
                <td>{klant === 'alle' && <span className="ttag">{b.tenant.name}</span>} {b.reason}{b.alert && <span className="muted"> · {b.alert.number}</span>}</td>
                <td className="hide-sm">{b.firewalls ?? <span className="muted">—</span>}</td>
                <td><St soort={blokStand(b.status)[0]}>{blokStand(b.status)[1]}</St></td>
                <td className="hide-sm muted">{b.status === 'actief' ? <span title={wanneer(b.expiresAt)}>{tot(b.expiresAt)}</span> : geleden(b.decidedAt ?? b.proposedAt)}</td>
              </tr>
            ))}{!lijst.filter(groepen[tab]).length && <tr><td colSpan={5} className="empty">{t('Niets in deze lijst.')}</td></tr>}</tbody>
          </table>
        )}
      </section>
    </>
  );
}
